/**
 * MobilePreview — Phase 6.A
 *
 * Full-page mobile preview powerhouse. Beats the existing right-rail
 * preview iframe in every dimension:
 *
 *   - Source modes:  live / arbitrary URL / drag-drop file / branch
 *                    preview (paste a CF Pages preview URL)
 *   - Device presets: iPhone 15 Pro · iPhone SE · iPad Pro 12.9 ·
 *                    Pixel 8 · Custom W×H
 *   - Side-by-side compare: two independent iframes with their own
 *                    source + device + scale, so you can A/B a branch
 *                    against production
 *   - Scale slider:  0.4× – 1.0× per pane
 *   - Per-pane reload, "open in new tab", and "screenshot" (via
 *                    canvas if the iframe is same-origin; falls back
 *                    to a clear copy-the-link hint otherwise)
 *
 * Files dragged in get a Blob URL so they render entirely client-side
 * — no upload to the worker. Convenient for testing local edits to
 * dashboard.json shape changes.
 */
import { html, type TemplateResult } from "lit-html";
import { renderExplainButton } from "./ExplainButton";

type SourceMode = "live" | "url" | "file" | "branch";

interface DevicePreset {
  id:    string;
  name:  string;
  w:     number;        // CSS px (logical)
  h:     number;
  chrome: "iphone" | "ipad" | "android" | "none";
  ratio?: string;       // e.g., "iPhone 15 Pro · 393×852"
}

// Most production users hit the user app on iPhone 16/17 Pro and Pro
// Max — those are first-class devices. iPhone 16 Pro and 17 Pro share
// the same logical viewport (Apple kept the form factor); same for
// the Max sizes. Older devices kept for completeness so legacy iOS
// regressions can be caught.
const DEVICES: DevicePreset[] = [
  { id: "iphone-17-pro",     name: "iPhone 17 Pro",     w: 402,  h: 874,  chrome: "iphone",  ratio: "402×874" },
  { id: "iphone-17-pro-max", name: "iPhone 17 Pro Max", w: 440,  h: 956,  chrome: "iphone",  ratio: "440×956" },
  { id: "iphone-16-pro",     name: "iPhone 16 Pro",     w: 402,  h: 874,  chrome: "iphone",  ratio: "402×874" },
  { id: "iphone-16-pro-max", name: "iPhone 16 Pro Max", w: 440,  h: 956,  chrome: "iphone",  ratio: "440×956" },
  { id: "iphone-15-pro",     name: "iPhone 15 Pro",     w: 393,  h: 852,  chrome: "iphone",  ratio: "393×852" },
  { id: "iphone-se",         name: "iPhone SE",         w: 375,  h: 667,  chrome: "iphone",  ratio: "375×667" },
  { id: "ipad-pro",          name: "iPad Pro 12.9",     w: 1024, h: 1366, chrome: "ipad",    ratio: "1024×1366" },
  { id: "pixel-8",           name: "Pixel 8",           w: 412,  h: 915,  chrome: "android", ratio: "412×915" },
  { id: "custom",            name: "Custom",            w: 402,  h: 874,  chrome: "none" },
];

interface PaneState {
  sourceMode:  SourceMode;
  url:         string;          // resolved URL fed to the iframe
  inputUrl:    string;          // user-entered URL (URL/branch mode)
  fileName:    string | null;   // for file mode
  deviceId:    string;
  scale:       number;
  customW:     number;
  customH:     number;
  reloadKey:   number;          // bumped to force iframe reload
}

interface ViewState {
  compare:     boolean;
  panes:       [PaneState, PaneState];
}

const PROD_LIVE_URL = "https://thekitchen.cloud";

const initialPane: PaneState = {
  sourceMode: "live",
  url:        PROD_LIVE_URL,
  inputUrl:   "",
  fileName:   null,
  deviceId:   "iphone-17-pro",       // most users target this device
  scale:      0.85,
  customW:    402,
  customH:    874,
  reloadKey:  0,
};

const view: ViewState = {
  compare: false,
  panes:   [{ ...initialPane }, {
    ...initialPane,
    sourceMode: "url",
    url:        "",
    deviceId:   "iphone-17-pro-max", // pane B defaults to Pro Max for compare
  }],
};

export function renderMobilePreview(onChange: () => void): TemplateResult {
  return html`
    <div class="mp">
      ${headerCard(onChange)}
      <div class="mp__stage ${view.compare ? "is-compare" : ""}">
        ${paneEl(view.panes[0], 0, onChange)}
        ${view.compare ? paneEl(view.panes[1], 1, onChange) : ""}
      </div>
    </div>
  `;
}

// =============================================================
// Header — global controls
// =============================================================
function headerCard(onChange: () => void): TemplateResult {
  return html`
    <section class="panel mp-header">
      <div class="mp-header__row">
        <div class="mp-header__title">
          <h3>Mobile preview</h3>
          <span class="mp-header__sub">
            chrome rendered with real backdrop-filter; iframe content owned by source domain
          </span>
        </div>

        <span class="mp-header__spacer"></span>

        <div class="mp-header__toggle">
          <button class="mp-toggle ${view.compare ? "" : "is-active"}"
                  @click=${() => { view.compare = false; onChange(); }}>
            single
          </button>
          <button class="mp-toggle ${view.compare ? "is-active" : ""}"
                  @click=${() => { view.compare = true;  onChange(); }}>
            compare
          </button>
        </div>

        ${renderExplainButton({
          label: "Explain preview",
          context: () => buildExplainContext(),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Pane (single device + controls)
// =============================================================
function paneEl(p: PaneState, idx: 0 | 1, onChange: () => void): TemplateResult {
  const device = DEVICES.find((d) => d.id === p.deviceId) ?? DEVICES[0]!;
  const w = device.id === "custom" ? p.customW : device.w;
  const h = device.id === "custom" ? p.customH : device.h;
  const isLive = p.sourceMode === "live" || p.url === PROD_LIVE_URL;

  return html`
    <section class="mp-pane">
      ${paneControls(p, idx, device, onChange)}

      <div class="mp-pane__stage">
        ${p.url ? deviceFrame(p, device, w, h, idx)
                : sourcePicker(p, idx, onChange)}
      </div>

      ${paneFooter(p, isLive, idx, onChange)}
    </section>
  `;
}

// ---- Top control row ----
function paneControls(
  p: PaneState, idx: 0 | 1,
  device: DevicePreset, onChange: () => void,
): TemplateResult {
  return html`
    <header class="mp-pane__head">
      <div class="mp-pane__source-tabs">
        ${(["live", "url", "file", "branch"] as SourceMode[]).map((m) => html`
          <button
            class="mp-stab ${p.sourceMode === m ? "is-active" : ""}"
            @click=${() => switchMode(p, m, onChange)}
          >${m}</button>
        `)}
      </div>

      <span class="mp-pane__head-spacer"></span>

      <select class="mp-pane__device"
              @change=${(e: Event) => {
                p.deviceId = (e.target as HTMLSelectElement).value;
                onChange();
              }}>
        ${DEVICES.map((d) => html`
          <option value=${d.id} ?selected=${d.id === p.deviceId}>
            ${d.name}${d.ratio ? ` (${d.ratio})` : ""}
          </option>
        `)}
      </select>

      <div class="mp-pane__scale">
        <label>scale</label>
        <input type="range" min="0.4" max="1.0" step="0.05"
               .value=${String(p.scale)}
               @input=${(e: Event) => {
                 p.scale = Number((e.target as HTMLInputElement).value);
                 onChange();
               }}/>
        <span class="mp-pane__scale-val">${(p.scale * 100).toFixed(0)}%</span>
      </div>

      <button class="mp-pane__btn"
              title="Reload iframe"
              @click=${() => { p.reloadKey++; onChange(); }}>
        ↻
      </button>

      ${idx === 0 ? "" : html`
        <button class="mp-pane__btn mp-pane__btn--danger"
                title="Mirror pane A"
                @click=${() => mirrorFromA(onChange)}>
          ⇇
        </button>
      `}

      ${void device, ""}
    </header>
  `;
}

// ---- Source-mode-specific input row ----
function sourcePicker(p: PaneState, _idx: 0 | 1, onChange: () => void): TemplateResult {
  switch (p.sourceMode) {
    case "live":
      return html`
        <div class="mp-blank">
          <p>Click to load production →</p>
          <button class="mp-btn mp-btn--primary"
                  @click=${() => {
                    p.url = PROD_LIVE_URL;
                    p.reloadKey++;
                    onChange();
                  }}>
            load thekitchen.cloud
          </button>
        </div>
      `;

    case "url":
      return html`
        <div class="mp-blank">
          <p>Paste an arbitrary URL:</p>
          <form class="mp-blank__form"
                @submit=${(e: Event) => {
                  e.preventDefault();
                  if (!p.inputUrl.trim()) return;
                  p.url = normalizeUrl(p.inputUrl.trim());
                  p.reloadKey++;
                  onChange();
                }}>
            <input class="mp-blank__input"
                   type="url"
                   placeholder="https://example.com"
                   .value=${p.inputUrl}
                   @input=${(e: Event) => {
                     p.inputUrl = (e.target as HTMLInputElement).value;
                   }}/>
            <button class="mp-btn mp-btn--primary" type="submit">load</button>
          </form>
        </div>
      `;

    case "file":
      return html`
        <div class="mp-blank mp-blank--drop"
             @dragover=${(e: DragEvent) => {
               e.preventDefault();
               (e.currentTarget as HTMLElement).classList.add("is-over");
             }}
             @dragleave=${(e: DragEvent) => {
               (e.currentTarget as HTMLElement).classList.remove("is-over");
             }}
             @drop=${(e: DragEvent) => {
               e.preventDefault();
               (e.currentTarget as HTMLElement).classList.remove("is-over");
               handleFileDrop(p, e, onChange);
             }}>
          <p>Drop an HTML / image file here</p>
          <p class="mp-blank__sub">
            renders client-side via Blob URL — never uploaded
          </p>
          <label class="mp-btn mp-btn--primary mp-blank__file">
            or pick a file
            <input type="file"
                   accept=".html,.htm,.png,.jpg,.jpeg,.svg,.json"
                   style="display:none;"
                   @change=${(e: Event) => {
                     const f = (e.target as HTMLInputElement).files?.[0];
                     if (f) handleFile(p, f, onChange);
                   }}/>
          </label>
        </div>
      `;

    case "branch":
      return html`
        <div class="mp-blank">
          <p>Paste a Cloudflare Pages preview URL:</p>
          <p class="mp-blank__sub">
            Each git branch on <code>dashboard-site</code> gets a unique
            preview URL like <code>&lt;hash&gt;.dashboard-site.pages.dev</code>.
            Find the latest one in
            <a href="https://dash.cloudflare.com" target="_blank"
               style="color:var(--accent-blue-light);">CF dashboard</a>
            → Pages → dashboard-site → Deployments.
          </p>
          <form class="mp-blank__form"
                @submit=${(e: Event) => {
                  e.preventDefault();
                  if (!p.inputUrl.trim()) return;
                  p.url = normalizeUrl(p.inputUrl.trim());
                  p.reloadKey++;
                  onChange();
                }}>
            <input class="mp-blank__input"
                   type="url"
                   placeholder="https://abc123.dashboard-site.pages.dev"
                   .value=${p.inputUrl}
                   @input=${(e: Event) => {
                     p.inputUrl = (e.target as HTMLInputElement).value;
                   }}/>
            <button class="mp-btn mp-btn--primary" type="submit">load</button>
          </form>
        </div>
      `;
  }
}

// ---- Device frame with chrome ----
function deviceFrame(
  p: PaneState, device: DevicePreset,
  w: number, h: number, idx: 0 | 1,
): TemplateResult {
  const scaledW = Math.round(w * p.scale);
  const scaledH = Math.round(h * p.scale);
  const chromeCls = `mp-device--${device.chrome}`;

  return html`
    <div class="mp-device ${chromeCls}"
         style="
           width:${scaledW}px;
           --device-w:${w}px;
           --device-h:${h}px;
           --device-scale:${p.scale};
         ">
      ${device.chrome === "iphone" ? html`<div class="mp-device__notch"></div>` : ""}
      ${device.chrome === "android" ? html`<div class="mp-device__camera"></div>` : ""}
      <div class="mp-device__screen"
           style="width:${scaledW}px; height:${scaledH}px;">
        <iframe
          class="mp-device__iframe"
          src=${`${p.url}${p.reloadKey > 0 ? (p.url.includes("?") ? "&" : "?") + "_r=" + p.reloadKey : ""}`}
          style="width:${w}px; height:${h}px;
                  transform: scale(${p.scale});
                  transform-origin: top left;"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          loading="lazy"
          title="preview ${idx}"
        ></iframe>
      </div>
    </div>
  `;
}

// ---- Bottom footer with URL + actions ----
function paneFooter(
  p: PaneState, isLive: boolean,
  _idx: 0 | 1, onChange: () => void,
): TemplateResult {
  if (!p.url) return html``;
  return html`
    <footer class="mp-pane__foot">
      <div class="mp-pane__foot-meta">
        ${isLive ? html`<span class="mp-pane__live-pill">LIVE</span>` : ""}
        <code class="mp-pane__url" title=${p.url}>${p.url.replace(/^https?:\/\//, "")}</code>
      </div>
      <div class="mp-pane__foot-actions">
        <button class="mp-pane__btn mp-pane__btn--small"
                @click=${() => { p.reloadKey++; onChange(); }}>
          reload
        </button>
        <a class="mp-pane__btn mp-pane__btn--small mp-pane__btn--link"
           href=${p.url} target="_blank" rel="noopener">
          open ↗
        </a>
        <button class="mp-pane__btn mp-pane__btn--small"
                @click=${() => clearPane(p, onChange)}
                title="Reset this pane">
          reset
        </button>
      </div>
    </footer>
  `;
}

// =============================================================
// Source mode + file handling
// =============================================================
function switchMode(p: PaneState, m: SourceMode, onChange: () => void): void {
  p.sourceMode = m;
  if (m === "live") {
    p.url = PROD_LIVE_URL;
    p.reloadKey++;
  } else {
    p.url = "";
    p.fileName = null;
  }
  onChange();
}

function handleFileDrop(p: PaneState, e: DragEvent, onChange: () => void): void {
  const f = e.dataTransfer?.files?.[0];
  if (f) handleFile(p, f, onChange);
}

function handleFile(p: PaneState, file: File, onChange: () => void): void {
  // Revoke previous Blob URL if any
  if (p.url.startsWith("blob:")) URL.revokeObjectURL(p.url);
  const blobUrl = URL.createObjectURL(file);
  p.url       = blobUrl;
  p.fileName  = file.name;
  p.reloadKey++;
  onChange();
}

function clearPane(p: PaneState, onChange: () => void): void {
  if (p.url.startsWith("blob:")) URL.revokeObjectURL(p.url);
  p.url       = p.sourceMode === "live" ? PROD_LIVE_URL : "";
  p.inputUrl  = "";
  p.fileName  = null;
  p.reloadKey = 0;
  onChange();
}

function mirrorFromA(onChange: () => void): void {
  const a = view.panes[0];
  view.panes[1] = {
    ...a,
    reloadKey: a.reloadKey + 1,
  };
  onChange();
}

function normalizeUrl(s: string): string {
  if (/^https?:\/\//i.test(s)) return s;
  return "https://" + s;
}

// =============================================================
// Explain context
// =============================================================
function buildExplainContext(): string {
  const lines = [
    `Mobile preview state:`,
    `  Compare mode: ${view.compare}`,
    "",
    `Pane A:`,
    `  source: ${view.panes[0].sourceMode}`,
    `  url: ${view.panes[0].url || "(none)"}`,
    `  device: ${view.panes[0].deviceId}`,
    `  scale: ${view.panes[0].scale}`,
  ];
  if (view.compare) {
    lines.push(
      "",
      `Pane B:`,
      `  source: ${view.panes[1].sourceMode}`,
      `  url: ${view.panes[1].url || "(none)"}`,
      `  device: ${view.panes[1].deviceId}`,
      `  scale: ${view.panes[1].scale}`,
    );
  }
  return lines.join("\n");
}
